import { PRODUCT_FORM_LIMITS } from '../../pages/home/products/components/product-form/constants';

// Action types
export const UPDATE_FORM_FIELD = 'UPDATE_FORM_FIELD';
export const SET_FIELD_VALIDATION = 'SET_FIELD_VALIDATION';
export const RESET_FORM = 'RESET_FORM';
export const INITIALIZE_FORM = 'INITIALIZE_FORM';
export const SET_FORM_EDITING = 'SET_FORM_EDITING';
export const UPDATE_FORM_VALIDATION = 'UPDATE_FORM_VALIDATION';

// Types
export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

export interface UpdateFormFieldAction {
  type: typeof UPDATE_FORM_FIELD;
  payload: {
    fieldName: string;
    value: any;
    valid?: boolean;
    error?: string | null;
  };
}

export interface SetFieldValidationAction {
  type: typeof SET_FIELD_VALIDATION;
  payload: {
    fieldName: string;
    valid: boolean;
    error?: string | null;
  };
}

export interface ResetFormAction {
  type: typeof RESET_FORM;
}

export interface InitializeFormAction {
  type: typeof INITIALIZE_FORM;
  payload: {
    product?: any;
    isEditing: boolean;
  };
}

export interface SetFormEditingAction {
  type: typeof SET_FORM_EDITING;
  payload: {
    isEditing: boolean;
    product?: any;
  };
}

export interface UpdateFormValidationAction {
  type: typeof UPDATE_FORM_VALIDATION;
}

export type ProductFormActionTypes = 
  | UpdateFormFieldAction
  | SetFieldValidationAction
  | ResetFormAction
  | InitializeFormAction
  | SetFormEditingAction
  | UpdateFormValidationAction;

// Action creators

/**
 * Update a form field value and optionally its validation
 * @param fieldName - Name of the field
 * @param value - Field value
 * @param valid - Optional validation state
 * @param error - Optional error message
 * @returns Redux action
 */
export const updateFormField = (
  fieldName: string, 
  value: any, 
  valid?: boolean, 
  error?: string | null
): UpdateFormFieldAction => ({
  type: UPDATE_FORM_FIELD,
  payload: {
    fieldName,
    value,
    valid,
    error
  }
});

/**
 * Set field validation without updating value
 * @param fieldName - Name of the field
 * @param valid - Validation state
 * @param error - Error message
 * @returns Redux action
 */
export const setFieldValidation = (
  fieldName: string, 
  valid: boolean, 
  error: string | null = null
): SetFieldValidationAction => ({
  type: SET_FIELD_VALIDATION,
  payload: {
    fieldName,
    valid,
    error
  }
});

/**
 * Reset form to initial state
 * @returns Redux action
 */
export const resetForm = (): ResetFormAction => ({
  type: RESET_FORM
});

/**
 * Initialize form with product data (for editing)
 * @param product - Product data
 * @param isEditing - Whether in editing mode
 * @returns Redux action
 */
export const initializeForm = (product: any = null, isEditing: boolean = false): InitializeFormAction => ({
  type: INITIALIZE_FORM,
  payload: {
    product,
    isEditing
  }
});

/**
 * Set form editing mode
 * @param isEditing - Whether in editing mode
 * @param product - Product data
 * @returns Redux action
 */
export const setFormEditing = (isEditing: boolean, product: any = null): SetFormEditingAction => ({
  type: SET_FORM_EDITING,
  payload: {
    isEditing,
    product
  }
});

/**
 * Update form validation state
 * @returns Redux action
 */
export const updateFormValidation = (): UpdateFormValidationAction => ({
  type: UPDATE_FORM_VALIDATION
});

// Validation helpers

/**
 * Validate product name field
 * @param value - Field value
 * @returns Validation result with valid flag and error message
 */
export const validateProductName = (value: string): ValidationResult => {
  const trimmedValue = value.trim();
  
  if (!trimmedValue) {
    return { valid: false, error: 'Product name is required' };
  }
  
  if (trimmedValue.length > PRODUCT_FORM_LIMITS.MAX_PRODUCT_NAME_LENGTH) {
    return { valid: false, error: `Product name must not exceed ${PRODUCT_FORM_LIMITS.MAX_PRODUCT_NAME_LENGTH} characters` };
  }
  
  const pattern = /^[A-Za-z0-9 _-]+$/;
  if (!pattern.test(trimmedValue)) {
    return { 
      valid: false, 
      error: 'Invalid Product Name. Only alpha-numerals, spaces, hyphen and underscore are allowed.' 
    };
  }
  
  return { valid: true, error: null };
};

/**
 * Validate product description field
 * @param value - Field value
 * @returns Validation result with valid flag and error message
 */
export const validateProductDescription = (value: string): ValidationResult => {
  if (value.length > PRODUCT_FORM_LIMITS.MAX_PRODUCT_DESCRIPTION_LENGTH) {
    return { valid: false, error: `Description must not exceed ${PRODUCT_FORM_LIMITS.MAX_PRODUCT_DESCRIPTION_LENGTH} characters` };
  }
  
  return { valid: true, error: null };
};

/**
 * Validate redirect URL
 * @param url - URL to validate
 * @returns Validation result with valid flag and error message
 */
export const validateRedirectUrl = (url: string): ValidationResult => {
  // Empty URLs are allowed (optional field)
  if (!url || url.trim() === '') {
    return { valid: true, error: null };
  }
  
  try {
    const urlObj = new URL(url);
    
    // Only HTTPS is allowed for security
    if (urlObj.protocol !== 'https:') {
      return { valid: false, error: 'Only HTTPS secure connections are allowed' };
    }
    
    return { valid: true, error: null };
  } catch (error) {
    return { valid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
  }
};